import { Link } from "react-router-dom";
import { ArrowRight, Languages, Stamp, Calculator, FileStack } from "lucide-react";
import { ADDITIONAL_SERVICES, IMAGES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { CTABand } from "@/components/CTABand";
import { PageHero } from "@/components/PageHero";
import { Reveal, ScaleIn } from "@/components/Motion";
import { ServiceCard } from "@/components/ServiceCard";

const benefits = [
  { icon: Languages, title: "Immigration-ready translations", text: "Certified translation coordination for civil records, passports, academic documents, and official correspondence." },
  { icon: Stamp, title: "Notary under one roof", text: "Convenient notarial support for affidavits, sworn statements, authorizations, and supporting paperwork." },
  { icon: Calculator, title: "Tax support for families", text: "Professional tax preparation that complements household, sponsorship, and financial documentation needs." },
  { icon: FileStack, title: "Organized document support", text: "Copies, forms, correspondence, and administrative preparation handled with the same care as immigration files." },
];

export default function AdditionalServices() {
  return (
    <>
      <PageHero
        eyebrow="Additional Services"
        titleLines={["One Trusted Office.", "Several Essential", "Professional Services."]}
        description="Immigration remains our primary focus. Egaal also provides certified translations, notary services, tax preparation, and document support so clients can manage related needs in one convenient place."
        image={IMAGES.documents}
        imageAlt="Professional document preparation"
      >
        <Reveal delay={0.62} className="mt-10 flex flex-col gap-4 sm:flex-row">
          <Button asChild size="lg" className="rounded-full bg-brand-copper px-8 text-brand-navy hover:bg-brand-copper/90">
            <Link to="/contact" data-testid="additional-hero-translation-cta">Request a Translation <ArrowRight className="h-4 w-4" /></Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-transparent px-8 text-white hover:bg-white hover:text-brand-navy">
            <Link to="/immigration-services" data-testid="additional-hero-immigration-cta">View Immigration Services</Link>
          </Button>
        </Reveal>
      </PageHero>
      <section className="section-padding" data-testid="additional-services-grid-section">
        <div className="section-shell">
          <Reveal className="mb-14 max-w-3xl">
            <p className="overline text-brand-copper">Complementary Services</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl lg:text-5xl">Professional support for the paperwork around your life.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {ADDITIONAL_SERVICES.map((service, index) => (
              <ServiceCard key={service.slug} service={service} index={index} compact />
            ))}
          </div>
        </div>
      </section>
      <section className="section-padding bg-white" data-testid="additional-benefits-section">
        <div className="section-shell grid gap-14 lg:grid-cols-[0.75fr_1.25fr]">
          <Reveal>
            <p className="overline text-brand-copper">Why It Helps</p>
            <h2 className="mt-4 font-serif text-3xl font-bold sm:text-4xl">Related needs, coordinated carefully.</h2>
            <p className="mt-6 text-base leading-8 text-brand-navy/66">
              Immigration matters often require translations, notarized statements, tax records, and organized copies. Egaal’s multi-service model reduces friction and keeps your documents moving through one trusted office.
            </p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-2">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <ScaleIn key={benefit.title} delay={index * 0.07} className="rounded-lg bg-brand-sand p-7">
                  <Icon className="h-7 w-7 text-brand-copper" />
                  <h3 className="mt-6 font-serif text-xl font-bold">{benefit.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-brand-navy/64">{benefit.text}</p>
                </ScaleIn>
              );
            })}
          </div>
        </div>
      </section>
      <CTABand id="additional-services" title="Need a translation, notary visit, tax appointment, or immigration consultation? Start here." />
    </>
  );
}
